// Thin platform-service client. Sends the operator JWT on every call;
// throws meaningful errors on non-2xx. The deploy/PATCH calls return the
// platform's full response (including `smoke` results when present) so the
// UI can show the LLM what actually happened.
//
// 401 handling — refresh + retry:
//   The platform's access JWT is short-lived (15 min by default). On the
//   first 401 of a call, we transparently exchange the refresh token for
//   a new access+refresh pair and retry the same request EXACTLY ONCE.
//   If the retry also returns 401, OR if the refresh itself failed, we
//   log out (refresh token is expired/revoked → operator must sign in
//   again). This keeps long sessions stable without the operator
//   bouncing back to /login every 15 minutes.
import { getToken, platformBase, logout, refreshAccessToken } from "./auth";
import { sessionLost, refreshFailureMessage } from "./lib/session_recovery";
import { parseRunStart, type RunStart } from "./lib/run_start";
import { streamWithReconnect, type SseEvent } from "./lib/sse_reconnect";

async function rawCall(method: string, path: string, body?: any, token?: string | null): Promise<Response> {
  return fetch(`${platformBase}${path}`, {
    method,
    headers: {
      "Content-Type":  "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

async function call<T>(method: string, path: string, body?: any): Promise<T> {
  let r = await rawCall(method, path, body, getToken());
  if (r.status === 401) {
    // Try to mint a new access token using the stored refresh token.
    // refreshAccessToken() coalesces concurrent callers so a burst of
    // parallel 401s triggers exactly ONE refresh round-trip.
    const refreshed = await refreshAccessToken();
    if (refreshed.ok) {
      r = await rawCall(method, path, body, refreshed.access);
    }
    if (r.status === 401) {
      // ON NE DÉCONNECTE QUE SI LA SESSION EST VRAIMENT PERDUE. Avant, tout échec de
      // rafraîchissement menait ici — y compris une simple coupure réseau, qui détruisait alors
      // une session de trente jours (voir lib/session_recovery.ts).
      const failure = refreshed.ok ? "refused" : refreshed.failure;
      if (sessionLost(failure)) logout();
      throw new Error(refreshFailureMessage(failure));
    }
  }
  const text = await r.text();
  const json = text ? (() => { try { return JSON.parse(text); } catch { return text; } })() : null;
  if (!r.ok) {
    // The server's whole answer rides on the error: a refusal that carries a
    // decision (a destructive publication's warning, a restore's price) is
    // shown as one, not painted red. Readers: PublishButton, BackupsPanel.
    // `message` is the sentence the server wrote for a person (a plan's
    // refusal says the numbers); `error` is the code. Show the sentence.
    const err: any = new Error(typeof json === "object" ? (json?.message ?? json?.error ?? r.statusText) : String(json));
    err.status = r.status; err.body = json;
    throw err;
  }
  return json as T;
}

// ── Endpoint wrappers ────────────────────────────────────────────────────
export type App = {
  slug: string;
  created_at: string;
  owner_id?: string;
  theme: string;
  font?: string;
  // Becomes true only when a generation run has been confirmed successful
  // end-to-end. The Workspace preview pane stays blurred (logo placeholder)
  // until this flips. Absent === false. Setter lands when the run/loop is
  // defined.
  run_succeeded?: boolean;
  // When a MOCKUP of this app was last shown for review — the preview has something to show before any
  // build succeeds (lib/preview_ready).
  mockup_shown_at?: string;
  // Archived (platform-service lib/app_archive.ts): its seat was given back, its work kept.
  // Nothing opens on it until it is brought back (POST /apps/:slug/unarchive).
  archived?: { at: string; reason: "trial-ended" | "taken-back" | "admin" };
  // `version` — the commit each environment is running. Absent on apps built
  // before the history existed, and on an app that has never deployed cleanly:
  // the UI must read that as "unknown", never as an error or as "none".
  dev:  { backend_slot: number; tenant_id: string; api_hostname: string; ui_hostname: string; api_port: number; version?: string };
  prod: { backend_slot: number; tenant_id: string; api_hostname: string; ui_hostname: string; api_port: number; version?: string };
};

export const listApps      = () => call<App[]>("GET", "/apps");

// `active_run_id` is the id of the build currently running for this
// app+operator (or null). The Workspace uses it to re-attach the SSE
// stream + show the Stop button after a page reload — without this,
// the UI loses its handle on the in-flight run.
// `attachments` — l'utilisateur peut-il joindre une capture ? Un réglage de PLATEFORME,
// donc à côté de l'app et non dedans. ABSENT VAUT NON : un serveur plus ancien qui ne
// connaît pas le champ ne doit pas faire apparaître un bouton que son API refuserait.
export const getApp        = (slug: string)               => call<{ app: App; active_run_id: string | null; attachments?: boolean }>("GET", `/apps/${slug}`);

// ── PARTAGE EN LECTURE (platform-service/lib/app_sharing) ────────────────────────────────────
// Une app partagée avec moi : ce que le serveur me montre d'elle (une liste blanche), et qui me
// l'a partagée. Un type à part, pas `App` : il n'a ni emplacements, ni ports, ni tenant — un
// écran de lecture qui lirait `app.dev.backend_slot` serait une erreur de compilation.
export type SharedAppView = {
  slug: string; created_at: string; theme: string; font?: string; run_succeeded?: boolean;
  dev:  { ui_hostname: string; version?: string };
  prod: { ui_hostname: string; version?: string };
};
export type SharedApp = { app: SharedAppView; shared_by: string | null };
export const listSharedApps = () => call<SharedApp[]>("GET", "/shared-apps");
/** La fiche telle qu'un LECTEUR la reçoit : `role: "viewer"`, et le run en cours du propriétaire. */
export const getSharedApp   = (slug: string) => call<{ app: SharedAppView; role?: "viewer"; active_run_id: string | null; shared_by?: string | null }>("GET", `/apps/${slug}`);
export type AppViewer = { sub: string; email: string | null; added_at: string };
export const listViewers  = (slug: string)                => call<{ viewers: AppViewer[] }>("GET", `/apps/${slug}/viewers`);
export const shareApp     = (slug: string, email: string) => call<{ ok: true; viewers: AppViewer[] }>("POST", `/apps/${slug}/viewers`, { email });
export const unshareApp   = (slug: string, sub: string)   => call<{ ok: true; viewers: AppViewer[] }>("DELETE", `/apps/${slug}/viewers/${encodeURIComponent(sub)}`);
export const createApp     = (slug: string, theme?: string, font?: string | null) =>
  call<{ ok: boolean; app: App }>("POST", "/apps", { slug, theme, ...(font !== undefined ? { font } : {}) });
export const setTheme      = (slug: string, theme: string)  => call<{ ok: boolean; app: App }>("PATCH", `/apps/${slug}`, { theme });
// `null` clears the operator override → app falls back to the theme's font.
export const setFont       = (slug: string, font: string | null) => call<{ ok: boolean; app: App }>("PATCH", `/apps/${slug}`, { font });

/** UNE RETOUCHE VISUELLE sur une photo de l'app : sa position, sa largeur, son ratio.
 *
 *  Le serveur écrit `frontend/visual.json` et redéploie le frontend SEUL, sans faire tourner
 *  l'agent (platform-service, POST /apps/:slug/visual). Donc : pas de jetons, pas de minutes,
 *  et aucun risque qu'il change autre chose au passage.
 *
 *  `unchanged: true` quand le geste n'a rien changé — l'opérateur a relâché sans bouger, et
 *  rien n'a été déployé. L'appelant n'a alors pas à recharger l'aperçu. */
export const setVisual = (slug: string, name: string, patch: Record<string, unknown>) =>
  call<{ ok: boolean; unchanged?: boolean; name?: string }>("POST", `/apps/${slug}/visual`, { name, patch });

// ── Versions ────────────────────────────────────────────────────────────────
// A version is a commit recorded at a deploy that BUILT CLEAN, so the list is
// "states that worked", not "things that happened".
export type Version = { sha: string; at: string; subject: string };

export const listVersions  = (slug: string) =>
  call<{ versions: Version[]; dev: string | null; prod: string | null }>("GET", `/apps/${slug}/versions`);

// Put DEV back on an earlier version. The server records this as a NEW version
// rather than rewriting history, so a revert can itself be reverted — which is
// the whole reason someone clicks it.
// ── Publishing ──────────────────────────────────────────────────────────────
// The SERVER decides publishability. If the UI computed it too they would drift,
// and a button would enable itself over a state the server refuses.
export type Publishable =
  | { ok: true; version: string }
  // `names`: with reason "secrets-unset", the declared secrets production has no value for.
  | { ok: false; reason: string; detail: string; names?: string[] };

// ── LE DOMAINE DU PROPRIÉTAIRE ──────────────────────────────────────────
//
// À la publication, l'app garde l'adresse que la plateforme lui donne gratuitement et peut EN PLUS
// répondre sous un domaine apporté par son propriétaire. Ces trois appels tiennent cette liste.
//
// AUCUNE VALIDATION ICI : la règle de ce qu'est un domaine acceptable vit côté serveur, qui rend un
// message écrit pour la personne. Une seconde validation dans le navigateur ferait deux vérités, et
// l'écran finirait par dire oui à ce que le serveur refuse.
export type CustomDomain = {
  hostname: string; provider_id: string; status: string; ssl_status: string;
  txt_name?: string; txt_value?: string; added_at: string;
  /** Le serveur a conclu que le nom ET son certificat sont actifs. */
  live?: boolean;
  /** Pourquoi l'état n'a pas pu être relu, s'il n'a pas pu l'être. */
  note?: string;
};
export type DnsLine = { type: "CNAME" | "TXT"; name: string; value: string; why: string };

export const listDomains   = (slug: string) =>
  call<{ ok: boolean; free_address: string; cname_target: string; domains: CustomDomain[] }>(
    "GET", `/apps/${slug}/domains`);

export const addDomain     = (slug: string, domain: string) =>
  call<{ ok: boolean; domain: CustomDomain; records: DnsLine[]; apex_note: string }>(
    "POST", `/apps/${slug}/domains`, { domain });

// Le nom voyage dans le CHEMIN, donc encodé : un domaine est de la saisie, et un caractère non
// encodé y changerait la route appelée.
export const removeDomain  = (slug: string, domain: string) =>
  call<{ ok: boolean; removed: string }>("DELETE", `/apps/${slug}/domains/${encodeURIComponent(domain)}`);

export const publishable   = (slug: string) => call<Publishable>("GET", `/apps/${slug}/publishable`);

// `confirm_destructive` is passed only after the operator has read what would be
// lost — the server refuses with the list until then.
// `version` names a ROLLBACK target — a version the ledger (GET /publications)
// knows; without it the current dev version is published.
export const publishApp    = (slug: string, confirmDestructive = false, version?: string) =>
  call<{ ok: boolean; version: string; rollback: boolean; first_publish: boolean; warning?: string }>(
    "POST", `/apps/${slug}/publish`, { ...(confirmDestructive ? { confirm_destructive: true } : {}), ...(version ? { version } : {}) });

// The ledger: what is live, and every version that has been (newest first).
export type Publication = { tag: string; sha: string; at: string };
export const publications  = (slug: string) => call<{ current: string | null; published: Publication[] }>("GET", `/apps/${slug}/publications`);

export const revertDev     = (slug: string, sha: string) =>
  call<{ ok: boolean; reverted_to: string; version?: string }>("POST", `/apps/${slug}/revert`, { sha });
export const resetDev      = (slug: string)                 => call<any>("POST", `/apps/${slug}/reset/dev`);
// Full wipe — releases backend+frontend slots, clears the conversation
// thread, removes the registry row. Frees the slot so a new app can
// take it. Operator-only, ownership-checked server-side. Returns
// partial_errors when one of the downstream releases failed but the
// registry row is still gone.
export const unarchiveApp  = (slug: string)                 => call<{ ok: boolean; slug: string }>("POST", `/apps/${slug}/unarchive`);
export const deleteApp     = (slug: string)                 =>
  call<{ ok: boolean; slug: string; partial_errors?: string[] }>("DELETE", `/apps/${slug}`);
export const events        = (slug: string, since = 0)      => call<{ events: any[] }>("GET", `/apps/${slug}/events/dev?since=${since}&limit=50`);

// ── Data view (owner-only): browse the app's own tenant DB ───────────
// Read-only. The platform proxies these to the deployer, which derives the
// tenant schema from the app's slot and reads every row as a schema-CONFINED
// admin role (never a cross-tenant superuser). See lib/data_view_proxy.
export type DbColumn = { name: string; type: string; nullable: boolean; references?: { table: string; column: string } };
export type DbTable  = { name: string; columns: DbColumn[]; primary_key: string[] };
// The row(s) a foreign-key value points at (the FK hover-preview).
export const lookupRow = (slug: string, refTable: string, refColumn: string, value: string) =>
  call<{ ok: boolean; rows: Record<string, unknown>[] }>(
    "GET", `/apps/${slug}/data/tables/${encodeURIComponent(refTable)}/lookup?column=${encodeURIComponent(refColumn)}&value=${encodeURIComponent(value)}`,
  );
// `call` throws the deployer's { error } on any non-2xx, so a resolved value
// always carries the data; `ok` is kept for shape-parity with the server.
export const listTables = (slug: string) =>
  call<{ ok: boolean; tables: DbTable[] }>("GET", `/apps/${slug}/data/tables`);

export type RowPage = { ok: boolean; rows: Record<string, unknown>[]; total: number; offset: number; limit: number };
export const listRows = (slug: string, table: string, offset = 0, limit = 50) =>
  call<RowPage>("GET", `/apps/${slug}/data/tables/${encodeURIComponent(table)}/rows?offset=${offset}&limit=${limit}`);

// Row writes. Values are sent as-is (strings are fine — the DB casts them to the
// column type); a `null` sets SQL NULL. Update/delete are keyed by the row's
// primary key. `call` throws the deployer's { error } on any non-2xx.
export type WriteResult = { ok: boolean; rows: Record<string, unknown>[]; affected: number };
const rowsPath = (slug: string, table: string) => `/apps/${slug}/data/tables/${encodeURIComponent(table)}/rows`;
export const insertRow = (slug: string, table: string, row: Record<string, unknown>) =>
  call<WriteResult>("POST", rowsPath(slug, table), { row });
export const updateRow = (slug: string, table: string, key: Record<string, unknown>, set: Record<string, unknown>) =>
  call<WriteResult>("PATCH", rowsPath(slug, table), { key, set });
export const deleteRow = (slug: string, table: string, key: Record<string, unknown>) =>
  call<WriteResult>("DELETE", rowsPath(slug, table), { key });
// ── Secrets (owner-only): set/list/clear the app's server-side secret VALUES ──
// The app DECLARES which secrets it needs (name + host) in its contract; the owner
// supplies the VALUE here. The platform proxies to the deployer, which seals the value
// before the DB and NEVER returns a value on list (only status). See data_view_proxy.
export type SecretStatus = { name: string; allowedHosts: string[]; purpose: string | null; isSet: boolean; updatedAt: string };
// Each environment has its own vault (a Stripe test key is not a Stripe key).
// `prod` is declared ahead of any publication from the app's contract, so its
// values can be set BEFORE publishing — the publish gate refuses while one is unset.
export type SecretsEnv = "dev" | "prod";
export const secretsPath = (slug: string, env: SecretsEnv, name = "") =>
  `/apps/${slug}/secrets${name ? `/${encodeURIComponent(name)}` : ""}${env === "prod" ? "?env=prod" : ""}`;
export const listSecrets = (slug: string, env: SecretsEnv = "dev") =>
  call<{ ok: boolean; secrets: SecretStatus[] }>("GET", secretsPath(slug, env));
// Set (or replace) a declared secret's value. Rejects (404) if the name isn't declared.
export const setSecret = (slug: string, name: string, value: string, env: SecretsEnv = "dev") =>
  call<{ ok: boolean }>("POST", secretsPath(slug, env), { name, value });
// Clear a value (keeps the declaration). `name` is UPPER_SNAKE (route-validated).
export const clearSecret = (slug: string, name: string, env: SecretsEnv = "dev") =>
  call<{ ok: boolean }>("DELETE", secretsPath(slug, env, name));

// ── « Se connecter avec… » — a secret the owner CONNECTS through the service's sign-in window ──
// (OAuth2 authorization code). Listed apart from the secrets: only those secrets have a window. The
// client id and client secret are ordinary entries above; this connects them to the owner's account
// at the service. Never a token here — the server keeps them sealed.
// `provider`: connected through PERIMETR's own app at the service (platform-oauth — "google"); the owner
// sets no value and registers no address. null: the owner's own client at the service.
export type SignInStatus = { name: string; status: "connected" | "reconnect" | null; expiresAtMs: number | null; updatedAt: string | null; provider: string | null };
const signInPath = (slug: string, env: SecretsEnv, rest = "") =>
  `/apps/${slug}/oauth${rest}${env === "prod" ? "?env=prod" : ""}`;
export const listSignIns = (slug: string, env: SecretsEnv = "dev") =>
  call<{ ok: boolean; grants: SignInStatus[] }>("GET", signInPath(slug, env));
// The service's sign-in page to open — or the reason it cannot open yet (a value not set).
export const startSignIn = (slug: string, name: string, env: SecretsEnv = "dev") =>
  call<{ ok: boolean; url?: string; error?: string }>("POST", signInPath(slug, env, `/${encodeURIComponent(name)}/start`));
// The window's answer, as the console received it (`?code=…&state=…`). The env came back with `state`.
export const finishSignIn = (slug: string, name: string, query: string) =>
  call<{ ok: boolean; error?: string }>("POST", `/apps/${slug}/oauth/${encodeURIComponent(name)}/finish`, { query });
// The answer at a PROVIDER's one address (/oauth/callback/:provider): `state` says which app and secret,
// and the answer names them back so the page can send the person to that app.
export const finishProviderSignIn = (provider: string, query: string) =>
  call<{ ok: boolean; error?: string; app?: string; name?: string }>("POST", `/oauth/finish/${encodeURIComponent(provider)}`, { query });
export const disconnectSignIn = (slug: string, name: string, env: SecretsEnv = "dev") =>
  call<{ ok: boolean; error?: string }>("DELETE", signInPath(slug, env, `/${encodeURIComponent(name)}`));

// ── BROWSER KEYS — publishable values that ship INSIDE the app's bundle ─────
//
// A separate surface from Secrets, and deliberately so: a secret's value never leaves the
// platform's vault, while these are compiled into the JavaScript every visitor downloads.
// So the panel SHOWS the value (hiding it would only stop the owner checking they pasted the
// publishable one) and the row carries the vendor's own prefixes, which the view module
// applies — one table, on the server, no copy here to drift.
export type BrowserKeyRow = {
  env: string;                  // the VITE_* name the app reads
  vendor: string;
  purpose: string;
  where: string;                // the vendor page where the owner gets it
  warning: string;              // what the owner must do about a key that ships publicly
  isSet: boolean;
  value: string | null;
  packages: readonly string[];
  mustStartWith: readonly string[];
  neverStartsWith: readonly string[];
};

export const browserKeysPath = (slug: string, env: SecretsEnv, name = "") =>
  `/apps/${slug}/browser-keys${name ? `/${encodeURIComponent(name)}` : ""}?env=${env}`;

export const listBrowserKeys = (slug: string, env: SecretsEnv = "dev") =>
  call<{ ok: boolean; env: SecretsEnv; keys: BrowserKeyRow[] }>("GET", browserKeysPath(slug, env));

export const setBrowserKey = (slug: string, name: string, value: string, env: SecretsEnv = "dev") =>
  call<{ ok: boolean; error?: string; note?: string }>("POST", browserKeysPath(slug, env), { name, value });

export const clearBrowserKey = (slug: string, name: string, env: SecretsEnv = "dev") =>
  call<{ ok: boolean; error?: string }>("DELETE", browserKeysPath(slug, env, name));

// ── Backups (owner-only, per environment) — design_backups_study.md §11 ──────
// The ledger of one environment's backups (newest first), the night's verdict
// on the latest, and the row counts NOW (what a restore would undo, in numbers).
export type BackupManifest = { stamp: string; taken_at: string; reason: "nightly" | "before-publish" | "before-restore" | "manual"; tables: Record<string, number>; files: { count: number; bytes: number } };
export type BackupsLedger = { ok: boolean; backups: BackupManifest[]; last: { stamp: string; ok: boolean; drill?: { ok: boolean; differences?: string[]; error?: string }; error?: string; at: string } | null; current_tables: Record<string, number> | null };
export const listBackups = (slug: string, env: SecretsEnv) => call<BackupsLedger>("GET", `/apps/${slug}/backups?env=${env}`);
export const takeBackup  = (slug: string, env: SecretsEnv) => call<{ ok: boolean; manifest?: BackupManifest; error?: string }>("POST", `/apps/${slug}/backups?env=${env}`);
// Without `confirm` the server answers 409 with the PRICE (warning + deltas); with it, the restore runs under the glass.
export const restoreBackup = (slug: string, env: SecretsEnv, stamp: string, confirm = false) =>
  call<{ ok: boolean; stamp: string; before?: string; error?: string; warning?: string }>("POST", `/apps/${slug}/restore`, { env, stamp, ...(confirm ? { confirm: true } : {}) });

// ── Files (owner-only): read the app's own source tree ───────────────
// READ-ONLY. Both endpoints already existed server-side and were simply never
// called from here; the platform routes each path to the deployer that owns it
// (backend paths bare, frontend under `frontend/`, e2e keeping its prefix) and
// checks ownership first. Nothing here can write — editing a file would need a
// push + redeploy, and the deployer refuses platform-owned paths anyway.
export type AppFile = { path: string; bytes: number };
export const listFiles = (slug: string, env: "dev" | "prod" = "dev") =>
  call<{ slug: string; env: string; files: AppFile[]; count: number; partial_errors?: string[] }>(
    "GET", `/apps/${slug}/files/${env}`);

// One file's text. The response is the DEPLOYER's own body, forwarded verbatim
// by the platform — `{ ok, file, bytes, content }`. A missing file is a 404 whose
// `{ error }` `call` turns into a thrown message, so the viewer shows the reason
// rather than an empty pane.
export const getSource = (slug: string, path: string, env: "dev" | "prod" = "dev") =>
  call<{ ok?: boolean; file?: string; bytes?: number; content: string }>(
    "GET", `/apps/${slug}/source/${env}?file=${encodeURIComponent(path)}`);

export type ThemePreview = { name: string; tokens: Record<string, string>; bg?: string };
export const themes        = () => call<{ themes: string[]; default: string; previews: ThemePreview[] }>("GET", "/themes");
// Curated Google Fonts. Names match Google's CSS2 API exactly (no
// translation) so the picker can build the preview <link> URL directly.
export type FontPreset = { name: string; category: "sans" | "serif" | "mono" | "display"; weights: string };
export const fonts         = () => call<{ fonts: FontPreset[]; default: string | null }>("GET", "/fonts");

// Operator-scoped LLM token totals. Returned by platform-service from
// lib/usage.ts. Flat counters — the platform picks the model, so we
// don't expose a by-model split.
export type Usage = {
  operator_id:       string;
  prompt_tokens:     number;
  completion_tokens: number;
  // Cumulative cached input tokens reported by the provider across
  // every call. 0 means no caching at all (no cache field returned, OR
  // every call was a full miss). UsageChip surfaces this so an operator
  // can see whether the platform's byte-stable prefix is actually paying
  // off.
  cached_tokens:     number;
  total_tokens:      number;
  call_count:        number;
  updated_at:        string;     // "" when the operator has never made a call
};
export const usage         = () => call<Usage>("GET", "/usage");

// ── Account ──────────────────────────────────────────────────────────────────
// The plan and what it allows, against what this month has spent
// (design_accounts.md §11). `plan` is null when the account has none.
// H = the hosting plan (€5 + VAT a month, or €50 a year, 2 apps, credit as top-ups). S, M, L are
// legacy: shown only to whoever holds one (platform-service/lib/plans.ts).
/** `credit_usd_per_month` is what the page shows (a plan credits its own price); `tokens_per_month` is
 *  the same credit in the ledger's units — the server's business, never displayed. */
export type Plan = {
  id: "S" | "M" | "L" | "H";
  price_usd?: number; price_eur?: number; price_eur_year?: number; vat?: "excluded";
  apps: number; credit_usd_per_month: number; tokens_per_month: number; gb: number; legacy: boolean;
};
export type TopUp = { id: "T10" | "T25" | "T50"; price_eur: number; credit_cents: number };
/** The free trial of a planless account in good standing (null otherwise). Dollars of credit, in cents. */
export type FreeView = {
  state: "open" | "running" | "ended"; since?: string; ends_at?: string;
  /** a free place is left on the node right now */
  seats_open: boolean;
  gift_cents: number; day_used_cents: number; day_limit_cents: number; run_limit_cents: number;
};
export type AccountView = {
  sub: string; email?: string;
  plan: Plan | null; plan_since?: string;
  // A cancellation's grace ends here (the plan stays until then); the night suspended the account here.
  plan_ends_at?: string; suspended_at?: string;
  month: string; renews_at: string;
  apps:    { used: number; allowed: number };
  // Dollars of build credit, in CENTS, never tokens: what is left (rounded down by the server),
  // what this month took, per app. The page formats; it never converts.
  credit:  { balance_cents: number; spent_this_month_cents: number; by_app_cents: Record<string, number> };
  // Bytes held (null = not measured), against the plan's gigabytes.
  storage: { used_bytes: number | null; allowed_gb: number; by_app: Record<string, number | null> };
  plans:   Plan[];
  /** what can be bought today */
  sold:    Plan["id"][];
  topups:  TopUp[];
  free:    FreeView | null;
  /** the terms version still to accept, or null — creating and building are refused (428) until then */
  terms_required: string | null;
  /** Whether the caller holds platform admin (PLATFORM_ADMIN_SUBS). Decides whether the
   *  platform-wide controls are rendered at all; the server enforces it again on every write. */
  is_platform_admin: boolean;
};
export const account       = () => call<AccountView>("GET", "/account");
// A hosted Freemius checkout link for a plan, e-mail locked to the account's.
/**
 * Ce que le serveur rend pour payer.
 *
 * `url` est TOUJOURS là, et c'est le repli : si le script de Freemius ne charge pas, on y va. Une
 * caisse qui ne s'ouvre pas est un client perdu.
 *
 * `overlay` n'arrive QUE pour un premier achat. Le serveur refuse de l'envoyer pour un changement
 * de plan, parce qu'une caisse en surimpression ouvre un achat NEUF : à quelqu'un qui a déjà une
 * licence, elle en vendrait une seconde (platform-service/lib/checkout_overlay).
 */
export type CheckoutOverlay = {
  product_id: string; plan_id: string; public_key: string; user_email: string; readonly_user: true; billing_cycle: "monthly";
};
// A hosted Freemius checkout for a one-time top-up of build credit. Its own licence: it never touches the plan.
export const topupLink     = (topup: TopUp["id"]) => call<{ ok: true; url: string; topup: TopUp["id"]; kind: "topup" }>("POST", "/account/topup", { topup });
export const checkoutLink  = (plan: Plan["id"]) => call<{ ok: true; url: string; plan: Plan["id"]; kind?: string; overlay?: CheckoutOverlay }>("POST", "/account/checkout", { plan });
// The client's rights (design_legal.md §2): accept the terms in force, take everything away, disappear.
export const acceptTerms   = (version: string) => call<{ ok: true; terms: { version: string; accepted_at: string } }>("POST", "/account/terms", { version });
// Platform-wide settings — ADMIN ONLY, and the server checks again on every call. The
// ceiling comes FROM the server: a page that hard-codes a bound drifts from the rule.
export type PlatformSettingsView = {
  read_batch: number; read_batch_ceiling: number;
  /** When the conversation is SUMMARISED, in tokens. The floor and the default come from
   *  the server too: a page that invents a bound drifts from the rule it displays. */
  compaction_threshold: number; compaction_floor: number; compaction_default: number;
  /** Which lanes run on the cheaper model: "off", "backend" or "all". The server decides
   *  what the words mean; this page only shows them. */
  cheap_lanes: "off" | "backend" | "all";
};
export const platformSettings    = () => call<PlatformSettingsView>("GET", "/platform/settings");
export const setReadBatch        = (read_batch: number) => call<PlatformSettingsView & { ok: true }>("POST", "/platform/settings", { read_batch });
export const setCompaction       = (compaction_threshold: number) => call<PlatformSettingsView & { ok: true }>("POST", "/platform/settings", { compaction_threshold });
export const setCheapLanes       = (cheap_lanes: PlatformSettingsView["cheap_lanes"]) => call<PlatformSettingsView & { ok: true }>("POST", "/platform/settings", { cheap_lanes });
export const exportAccount = () => call<unknown>("GET", "/account/export");
export const eraseAccount  = (confirm: string) => call<{ ok: true; erased: { apps: string[]; account: true }; partial_errors?: string[] }>("DELETE", "/account", { confirm });

// ── Conversation (chat + build) ─────────────────────────────────────
//
// Persisted by platform-service (lib/thread_store.ts). Each message is
// either a user turn or an assistant turn; `mode` records which pill
// the operator had selected at the time so a reloaded thread renders
// build runs distinctly from chat replies.
export type Message = {
  role: "user" | "assistant";
  text: string;
  mode: "chat" | "build";
  ts:   string;
  // Client-synthesized "thinking" item, derived live from the run's
  // `reasoning` SSE event — NOT part of the server-persisted thread (so a
  // reloaded thread has none; they reappear only while a run streams). When
  // set, `text` holds the model's chain-of-thought and `reasoning_tokens`
  // its provider-reported token count (0 when the provider doesn't expose
  // one). The UI renders these as a collapsible "Thought for N tokens"
  // block instead of a normal assistant bubble. A normal message leaves
  // `kind` undefined.
  //
  // "verification" — the PLATFORM's own verdict on whether the app was proved to
  // work, synthesized from the run's `e2e-corroboration` event. It sits beside
  // the agent's closing words on purpose: the agent writes free prose, and prose
  // is not checkable. canva, 2026-09-06, closed with "No JSX syntax error was
  // present; all tags are properly closed" — true of the app, false of the
  // record (the same agent had fixed that error forty seconds earlier), while the
  // machine's verdict for that run was UNSUPPORTED and was published to nobody.
  // "plan" — the splitting announced a plan for a compound request: `plan_parts` holds each
  // part's title in build order, and `plan_requirements` the operator's own words for what they
  // asked, so they can read the map before anything runs. It is a MILESTONE in the feed
  // (lib/run_feed): the one row the whole run hangs off.
  //
  // "part" — a part opened or closed. `part_index`/`part_total` give the "3 / 10" the operator
  // keeps asking for ("where are we at"), `part_title` names it, and `part_state` says whether it
  // is running, sealed or failed.
  // "failure" — the build stopped, and WHY, in a sentence the platform authored (server-side
  // lib/failure_notice). Never a provider's words: on 2026-09-22 a run died on a supplier billing
  // error, the feed said nothing, and the operator had to ask why — while the raw cause named our
  // supplier and our billing state, which is not their business. A MILESTONE in the feed: a run that
  // dies must say so in a line that is never folded.
  // "plan-closed" — the operator put the plan away. The strip disappears; this line stays and NAMES
  // the parts that were never built, because the strip was the only other place they were listed and
  // work disappearing in silence is the failure this codebase refuses everywhere else.
  kind?:             "reasoning" | "verification" | "plan" | "part" | "failure" | "plan-closed" | "review";
  reasoning_tokens?: number;
  plan_requirements?: string[];
  /**
   * The parts, in build order, WITH their ids.
   *
   * Objects rather than a list of titles: the operator clicks a part to start it (PlanStrip), and a
   * click needs the id the server knows it by. Two parallel arrays — titles here, ids there — would
   * be one reorder away from starting the wrong part.
   */
  plan_parts?:        Array<{ id: string; title: string }>;
  /** Which part this marker is about. Needed for the same reason as above. */
  part_id?:           string;
  /** On a "plan-closed" item: the titles of the parts that were never built. */
  plan_remaining?:    string[];
  part_index?:        number;
  part_total?:        number;
  part_title?:        string;
  part_state?:        "running" | "sealed" | "failed";
  /**
   * How long a part took, in ms — only on a closed part, and only when the run said so.
   *
   * Absent rather than 0 when unknown, for the reason StepsSummary.ms gives at length: a duration
   * printed as "0s" is a false fact, and this is the number the operator uses to guess when the
   * plan will finish.
   */
  part_ms?:           number;
  // WHICH AGENT wrote this turn. A build run has two agents working at once and
  // their turns interleave in one thread; the run view used to show the lane only
  // on the live action indicator, which the next tool call replaced — so the
  // attribution lasted a second and a reload had none of it. The server now
  // persists it (lib/thread_store.Message.lane), so the badge survives a reload.
  //
  // Absent means UNKNOWN — a chat reply, a single-agent run, or a turn written
  // before the field existed. The UI renders no badge then; it never guesses.
  lane?:             "backend" | "frontend";
};

// GET the conversation for an app (used on page mount to replay).
// `ask_pending` travels beside them too, and for the same reason the plan does: a question the run
// is waiting on lives in the run's event stream, so a reload lost it and the operator came back to
// a brief they had been asked to confirm and nothing to confirm it with.
//
// `plan_events` travels BESIDE the messages, not among them: the plan is machinery, not conversation,
// and the thread is what the model is primed from. Absent on an app with no plan, and absent from a
// server older than this field — so every reader treats it as optional.
export const getThread = (slug: string) =>
  call<{ messages: Message[]; plan_events?: unknown[]; ask_pending?: unknown; inbox?: unknown }>("GET", `/apps/${slug}/thread`);

// CHAT mode — a single grounded, read-only answer about the app. No tools, no
// edits. The server injects the app's contract so the answer reads the real app,
// appends both turns, and returns the full thread to replace state authoritatively.
export const postChat = (slug: string, prompt: string, joints?: RunAttachments) =>
  call<{ ok: boolean; user: Message; assistant: Message; thread: Message[]; events: any[] }>(
    // LE MÊME CORPS QUE /run, par la MÊME fonction. Sa remarque du 2026-10-07 : « le mode chat
    // ne voit pas les photos ni les fichiers ». Le trombone s appelle pourtant dans les deux
    // modes — il dépend de la capacité multimodale, jamais du mode — donc on pouvait joindre une
    // photo, la voir attachée, l envoyer, et elle n arrivait nulle part : cette fonction
    // n envoyait que `{ prompt }`.
    //
    // `runBody` est réemployée telle quelle plutôt qu une seconde forme écrite ici : la règle
    // qu elle tient — une demande ordinaire reste `{ prompt }` et rien d autre — vaut autant
    // pour le chat, et deux copies auraient divergé.
    "POST", `/apps/${slug}/chat`, runBody(prompt, joints),
  );

// ACT mode — makes changes. Starts the run loop; the server auto-picks build (a
// new app) or edit (a sealed app) from the app's phase. Returns the run id; events
// stream via SSE. Chat-vs-Act is chosen EXPLICITLY by the operator's toggle — the
// system never guesses that axis (build-vs-edit, which it CAN know from state, it
// still decides).
// See lib/run_start.ts for the 409-means-reattach contract this returns.
export type { RunStart as PostRunResult } from "./lib/run_start";
import { type RunAttachments, type RunBody, runBody } from "./lib/run_body";
export async function postRun(slug: string, prompt: string, joints?: RunAttachments): Promise<RunStart> {
  // LA FORME DU CORPS EST UNE DÉCISION, et elle vit dans src/lib/run_body : une demande
  // ordinaire reste `{ prompt }` et rien d'autre, et cette règle est éprouvée là-bas sur le
  // COMPORTEMENT (10 tests, 8 mutations) au lieu d'une comparaison de chaîne ici.
  //
  // Les BORNES ne sont nulle part dans ce fichier : elles font foi côté serveur
  // (lib/image_intake, lib/document_intake) et sont annoncées à l'utilisateur par le
  // crochet. Une troisième copie divergerait des deux autres.
  return postRunBody(slug, runBody(prompt, joints));
}

/**
 * Start ONE named part of the plan — the operator clicked it in the strip above the chat.
 *
 * No prompt is sent. The part's text comes from the plan the server holds, because a client that
 * could send the text could send ANY text and have it recorded as that part.
 */
export async function postRunPart(slug: string, partId: string): Promise<RunStart> {
  return postRunBody(slug, { part_id: partId });
}

/**
 * Answer a pending question by CLICKING one of its options.
 *
 * The index, not the label. The panel used to write the option's text into the composer and make
 * the operator press Send, so the one certain fact — which option was clicked — was thrown away
 * and the server read it back out of a sentence. Measured 2026-09-23: "oui", "parfait" and
 * "ça me va" were all read as change requests, so a French operator approving their own plan had
 * it taken as a correction.
 *
 * `prompt` rides along for the option that means "and here is what I want changed": the index says
 * WHICH answer, the text is the request.
 */
export async function postRunAnswer(slug: string, optionIndex: number, prompt?: string, questionId?: string): Promise<RunStart> {
  // `question_id` names WHICH question this click answers — several can wait in the server's
  // queue, and an index alone would land on whichever was at its head.
  return postRunBody(slug, { option_index: optionIndex, ...(prompt ? { prompt } : {}), ...(questionId ? { question_id: questionId } : {}) });
}

/**
 * Start the next run on what the operator QUEUED while the last one worked (lib/operator_inbox). No
 * text is sent: the words come from the queue the server holds, as a plan's part does.
 */
export async function postRunQueued(slug: string): Promise<RunStart> {
  return postRunBody(slug, { from_inbox: true });
}

/**
 * Queue a message while a run works. `noRun` — the run had ended by the time it arrived: NOTHING was
 * queued, and the words must not be sent on their own (an answer may now be owed; the caller keeps
 * them in the composer).
 */
export async function queueMessage(slug: string, text: string): Promise<{ noRun: false; inbox: unknown } | { noRun: true }> {
  try {
    const r = await call<{ inbox: unknown }>("POST", `/apps/${slug}/inbox`, { text });
    return { noRun: false, inbox: r.inbox };
  } catch (e: any) {
    if (e?.status === 409 && e?.body?.no_run === true) return { noRun: true };
    throw e;
  }
}
export const editQueuedMessage = (slug: string, id: string, text: string) =>
  call<{ inbox: unknown }>("PATCH", `/apps/${slug}/inbox/${id}`, { text });
export const removeQueuedMessage = (slug: string, id: string) =>
  call<{ inbox: unknown }>("DELETE", `/apps/${slug}/inbox/${id}`);

/** The operator is done with this plan: close it. Refused while a part is being built. */
export const closePlan = (slug: string) =>
  call<{ ok: boolean; remaining?: number; error?: string }>("POST", `/apps/${slug}/plan/close`, {});

// Les pièces jointes ne s'ajoutent QUE sur la branche d'une demande écrite : une part de plan nommée et
// une réponse à une question ne portent pas de pièce jointe — la première tire son texte du
// plan que le serveur tient, la seconde est un index.
async function postRunBody(slug: string, body: RunBody | { part_id: string } | { option_index: number; prompt?: string; question_id?: string } | { from_inbox: true }): Promise<RunStart> {
  let r = await rawCall("POST", `/apps/${slug}/run`, body, getToken());
  if (r.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed.ok) r = await rawCall("POST", `/apps/${slug}/run`, body, refreshed.access);
    if (r.status === 401) {
      const failure = refreshed.ok ? "refused" : refreshed.failure;
      if (sessionLost(failure)) logout();
      throw new Error(refreshFailureMessage(failure));
    }
  }
  const text = await r.text();
  const json: unknown = text ? (() => { try { return JSON.parse(text); } catch { return null; } })() : null;
  return parseRunStart(r.status, json, r.statusText);
}

// Operator-triggered cancellation of an in-flight build run. Trips the
// server-side AbortSignal; the orchestrator exits at the next iteration
// boundary (or immediately if a provider call is in flight). The SSE
// stream closes cleanly with a final `run-end` event of status "aborted".
export const abortRun = (slug: string, runId: string) =>
  call<{ ok: boolean; run_id: string; status: string }>("POST", `/apps/${slug}/run/${runId}/abort`);

// SSE consumer for /run/:id/events. Browser `EventSource` doesn't
// support custom headers (no Authorization), so we use fetch with a
// ReadableStream and parse the `data: <json>\n\n` envelope manually.
// This is the standard clean approach when an authed SSE feed is
// required (used by GitHub Copilot, Claude Code, etc.).
//
// Reconnect: a stream that closes WITHOUT a run-end event (network
// hiccup, browser tab idle, proxy timeout) is auto-reconnected from
// the next unseen seq via lib/sse_reconnect — the server's ?since=N
// replay gives us exact resume. Without this, a mid-build drop leaves
// the UI silent while the server keeps generating events.
//
// Yields each parsed event to `onEvent`. Resolves when the stream
// closes naturally (server sent run-end then close), or when `signal`
// aborts. Throws if the reconnect budget is exhausted.
export async function subscribeRunEvents(
  slug:    string,
  runId:   string,
  since:   number,
  onEvent: (e: any) => void,
  signal:  AbortSignal,
): Promise<void> {
  return streamWithReconnect(
    (cursor, onEv, sig) => streamOnce(slug, runId, cursor, onEv, sig),
    onEvent,
    signal,
    { initialSince: since },
  );
}

// One stream pass — opens the fetch, parses SSE frames, yields events
// via onEvent. Resolves when the server closes the stream (clean or
// mid-flight); throws on HTTP / network errors. The reconnect wrapper
// in subscribeRunEvents distinguishes "run finished" (saw run-end)
// from "drop" (didn't) and re-invokes us with the next cursor.
async function streamOnce(
  slug:    string,
  runId:   string,
  since:   number,
  onEvent: (e: SseEvent) => void,
  signal:  AbortSignal,
): Promise<void> {
  const url = `${platformBase}/apps/${slug}/run/${runId}/events?since=${since}`;
  let r = await fetch(url, {
    headers: { Authorization: `Bearer ${getToken()}` },
    signal,
  });
  // Same refresh-then-retry pattern as `call`. SSE streams can live for
  // many minutes — opening a connection right at the moment the access
  // token expires would force a logout otherwise.
  if (r.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed.ok) {
      r = await fetch(url, { headers: { Authorization: `Bearer ${refreshed.access}` }, signal });
    }
    if (r.status === 401) {
      const failure = refreshed.ok ? "refused" : refreshed.failure;
      if (sessionLost(failure)) logout();
      throw new Error(refreshFailureMessage(failure));
    }
  }
  if (!r.ok || !r.body) throw new Error(`SSE failed: HTTP ${r.status}`);
  const reader  = r.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    // SSE frames are separated by `\n\n`. Each frame has one or more
    // `field: value` lines — we only care about `data:` (the JSON
    // payload). Ignore `event:`, `id:`, comments (`:` prefix), etc.
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      // A frame may have multiple `data:` lines; concatenate them
      // with newlines per the SSE spec.
      const dataLines = frame.split("\n").filter(l => l.startsWith("data:")).map(l => l.slice(5).replace(/^ /, ""));
      if (dataLines.length === 0) continue;
      try {
        const event = JSON.parse(dataLines.join("\n"));
        onEvent(event);
      } catch {
        // A malformed frame is non-fatal — skip and keep reading.
      }
    }
  }
}
